import React from 'react';
import { Button } from 'components/Button';

/** Inline "a reference list failed to load" banner, with a retry.
 *
 *  The `common` slice's master-data thunks (CASA, employees, positions, sizes…)
 *  are dispatched once at app start from `Routes/Approuter/index.jsx` and are
 *  never retried on their own. Until the slice was fixed, a failed fetch left
 *  its `…Loading` flag true forever, and every `FilterSelect` bound to it —
 *  `isDisabled={isDisabled || isLoading}` — sat permanently disabled reading
 *  "Loading…", with nothing on screen to say why or any way to recover.
 *
 *  The slice now clears the flag and sets a matching `…Error`. This component
 *  is the surface for that error, and belongs wherever the resulting empty
 *  picker actually BLOCKS the task — a required field, or the only control that
 *  can produce the screen's output. A picker that merely narrows a list which
 *  still renders does not need one; it degrades to "no filter available",
 *  which is legible on its own and not worth a banner on every screen.
 *
 *  Render it with `show={xError && !xLoading}` so a retry already in flight
 *  replaces the banner with the control's own loading state rather than showing
 *  both at once.
 *
 *  @param {boolean} show      Whether the live region is filled. The region
 *                             itself is always mounted; see below.
 *  @param {() => void} onRetry  Re-dispatches the thunk that failed.
 *  @param {React.ReactNode} children  What could not be loaded, and what that
 *                                     stops the user doing, in plain words.
 */
const MasterDataRetryAlert = ({ show, onRetry, children, className = '' }) => (
  // The live region is ALWAYS mounted and only its contents are toggled.
  // A `role="alert"` node that mounts together with its text announces
  // nothing: assistive tech watches an existing region for changes, and an
  // element that arrives already-populated is just part of the page. That is
  // the primary case here, not an edge one — these thunks are dispatched at
  // app start, so by the time the screen is opened the rejection has usually
  // already happened and the banner renders on first paint.
  //
  // An empty region has no layout footprint, so this costs the callers
  // nothing while `show` is false.
  <div role="alert">
    {show ? (
      <div
        className={`alert alert-warning d-flex justify-content-between align-items-center flex-wrap gap-2 ${className}`.trim()}
      >
        <span>
          <i className="fa fa-exclamation-triangle me-2" aria-hidden="true" />
          {children}
        </span>
        <Button
          label="Retry"
          icon="fa fa-refresh"
          className="btn btn-sm btn-outline-secondary"
          onClick={onRetry}
        />
      </div>
    ) : null}
  </div>
);

export default MasterDataRetryAlert;
